import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import AdminLayout from './components/layout/AdminLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import MenuAccessGuard from './components/auth/MenuAccessGuard'

// Simple loading component
function LoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '18px'
    }}>
      로딩 중...
    </div>
  )
}

// Simple error boundary test
function TestPage() {
  return (
    <div style={{ padding: '20px' }}>
      <h1>테스트 페이지</h1>
      <p>이 페이지가 보이면 라우팅이 정상입니다.</p>
    </div>
  )
}

// Lazy load pages
const Login = lazy(() => import('./pages/Login'))
const ForcePasswordChange = lazy(() => import('./pages/ForcePasswordChange'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const BoardList = lazy(() => import('./pages/board/BoardList'))
const BoardContentManageList = lazy(() => import('./pages/board/BoardContentManageList'))
const BoardDetail = lazy(() => import('./pages/board/BoardDetail'))
const BoardForm = lazy(() => import('./pages/board/BoardForm'))
const BoardContentList = lazy(() => import('./pages/board/BoardContentList'))
const BoardContentForm = lazy(() => import('./pages/board/BoardContentForm'))
const BoardContentListBySlug = lazy(() => import('./pages/board/BoardContentListBySlug'))
const BoardContentFormBySlug = lazy(() => import('./pages/board/BoardContentFormBySlug'))
const EventList = lazy(() => import('./pages/board/EventList'))
const EventForm = lazy(() => import('./pages/board/EventForm'))
const ManagerList = lazy(() => import('./pages/member/ManagerList'))
const ManagerForm = lazy(() => import('./pages/member/ManagerForm'))
const MemberList = lazy(() => import('./pages/member/MemberList'))
const MemberDetail = lazy(() => import('./pages/member/MemberDetail'))
const AuthorityList = lazy(() => import('./pages/member/AuthorityList'))
const Profile = lazy(() => import('./pages/member/Profile'))
const AuthorityMenuAssign = lazy(() => import('./pages/member/AuthorityMenuAssign'))
const ManagerAuthAssign = lazy(() => import('./pages/member/ManagerAuthAssign'))
const LoginHistoryList = lazy(() => import('./pages/member/LoginHistoryList'))
const InfoQueryHistoryList = lazy(() => import('./pages/member/InfoQueryHistoryList'))
const InfoChangeHistoryList = lazy(() => import('./pages/member/InfoChangeHistoryList'))
const NoShowMemberList = lazy(() => import('./pages/member/NoShowMemberList'))
const SchoolMemberList = lazy(() => import('./pages/member/SchoolMemberList'))
const SchoolMemberForm = lazy(() => import('./pages/member/SchoolMemberForm'))
// 예약/신청관리
const CounselingList = lazy(() => import('./pages/rsap/CounselingList'))
const CounselingDetail = lazy(() => import('./pages/rsap/CounselingDetail'))
const CareReservationList = lazy(() => import('./pages/rsap/CareReservationList'))
const CareReservationDetail = lazy(() => import('./pages/rsap/CareReservationDetail'))
// 시설및정보관리
const FacilityList = lazy(() => import('./pages/fclt/FacilityList'))
const FacilityForm = lazy(() => import('./pages/fclt/FacilityForm'))
const CareFacilityList = lazy(() => import('./pages/fclt/CareFacilityList'))
const CareFacilityForm = lazy(() => import('./pages/fclt/CareFacilityForm'))
const ExperienceFacilityList = lazy(() => import('./pages/fclt/ExperienceFacilityList'))
const ExperienceFacilityForm = lazy(() => import('./pages/fclt/ExperienceFacilityForm'))
const TourismFacilityList = lazy(() => import('./pages/fclt/TourismFacilityList'))
const TourismFacilityForm = lazy(() => import('./pages/fclt/TourismFacilityForm'))
const MedicalFacilityList = lazy(() => import('./pages/fclt/MedicalFacilityList'))
const MedicalFacilityForm = lazy(() => import('./pages/fclt/MedicalFacilityForm'))
const WelfareList = lazy(() => import('./pages/fclt/WelfareList'))
const WelfareForm = lazy(() => import('./pages/fclt/WelfareForm'))
const ProgramList = lazy(() => import('./pages/fclt/ProgramList'))
const ProgramForm = lazy(() => import('./pages/fclt/ProgramForm'))
const ProgramSatisfactionList = lazy(() => import('./pages/fclt/ProgramSatisfactionList'))
const SatisfactionResultList = lazy(() => import('./pages/fclt/SatisfactionResultList'))
const SatisfactionResultDetail = lazy(() => import('./pages/fclt/SatisfactionResultDetail'))
const SatisfactionStatsPage = lazy(() => import('./pages/fclt/SatisfactionStatsPage'))
const ProgramApplicationList = lazy(() => import('./pages/fclt/ProgramApplicationList'))
const ProgramApplicationForm = lazy(() => import('./pages/fclt/ProgramApplicationForm'))
// 홈페이지관리
const MainList = lazy(() => import('./pages/homepage/MainList'))
const MainForm = lazy(() => import('./pages/homepage/MainForm'))
const HtmlContentList = lazy(() => import('./pages/homepage/HtmlContentList'))
const HtmlContentForm = lazy(() => import('./pages/homepage/HtmlContentForm'))
const PrivacyPolicyList = lazy(() => import('./pages/homepage/PrivacyPolicyList'))
const PrivacyPolicyForm = lazy(() => import('./pages/homepage/PrivacyPolicyForm'))
const SmsHistoryList = lazy(() => import('./pages/homepage/SmsHistoryList'))
const SatisfactionList = lazy(() => import('./pages/homepage/SatisfactionList'))
const RecommendProgramList = lazy(() => import('./pages/homepage/RecommendProgramList'))
const RecommendProgramForm = lazy(() => import('./pages/homepage/RecommendProgramForm'))
// 통계관리
const AccessStatistics = lazy(() => import('./pages/stat/AccessStatistics'))
const PageStatistics = lazy(() => import('./pages/stat/PageStatistics'))
const AdminAccessLog = lazy(() => import('./pages/stat/AdminAccessLog'))
const HourStat = lazy(() => import('./pages/stat/HourStat'))
const MnthStat = lazy(() => import('./pages/stat/MnthStat'))
const YearStat = lazy(() => import('./pages/stat/YearStat'))
const MenuDaysStat = lazy(() => import('./pages/stat/MenuDaysStat'))
const MenuMontStat = lazy(() => import('./pages/stat/MenuMontStat'))
const MemberStat = lazy(() => import('./pages/stat/MemberStat'))
const FacilityLog = lazy(() => import('./pages/stat/FacilityLog'))
const CareProgramStat = lazy(() => import('./pages/stat/CareProgramStat'))
const ReservationStat = lazy(() => import('./pages/stat/ReservationStat'))
const ErrorLog = lazy(() => import('./pages/stat/ErrorLog'))
const BannerList = lazy(() => import('./pages/content/BannerList'))
const BannerForm = lazy(() => import('./pages/content/BannerForm'))
const PopupList = lazy(() => import('./pages/content/PopupList'))
const PopupForm = lazy(() => import('./pages/content/PopupForm'))
const FaqList = lazy(() => import('./pages/content/FaqList'))
const FaqForm = lazy(() => import('./pages/content/FaqForm'))
const CodeList = lazy(() => import('./pages/code/CodeList'))
const CodeTypeForm = lazy(() => import('./pages/code/CodeTypeForm'))
const CodeDetail = lazy(() => import('./pages/code/CodeDetail'))
const MenuList = lazy(() => import('./pages/menu/MenuList'))
const UIShowcase = lazy(() => import('./pages/UIShowcase'))
const SitemapPage = lazy(() => import('./pages/menu/SitemapPage'))
// Placeholder (미개발 메뉴)
const PlaceholderPage = lazy(() => import('./pages/placeholder/PlaceholderPage'))
const ProviderMemberDetail = lazy(() => import('./pages/placeholder/ProviderMemberDetail'))
// AI 챗봇관리 — CHATBOT DISABLED
// const ChatbotDashboard = lazy(() => import('./pages/chatbot/ChatbotDashboard'))
// const ChatbotDocuments = lazy(() => import('./pages/chatbot/ChatbotDocuments'))
// const ChatbotDocumentUpload = lazy(() => import('./pages/chatbot/ChatbotDocumentUpload'))
// const ChatbotCategories = lazy(() => import('./pages/chatbot/ChatbotCategories'))
// const ChatbotAuditLogs = lazy(() => import('./pages/chatbot/ChatbotAuditLogs'))
// const ChatbotUsage = lazy(() => import('./pages/chatbot/ChatbotUsage'))
// const ChatbotPlayground = lazy(() => import('./pages/chatbot/ChatbotPlayground'))
// const ChatbotSettingsPage = lazy(() => import('./pages/chatbot/ChatbotSettings'))
// const ChatbotQnaList = lazy(() => import('./pages/chatbot/ChatbotQnaList'))
// const ChatbotQnaForm = lazy(() => import('./pages/chatbot/ChatbotQnaForm'))
// const ChatbotQnaCategories = lazy(() => import('./pages/chatbot/ChatbotQnaCategories'))
// const ChatbotQnaTagManage = lazy(() => import('./pages/chatbot/ChatbotQnaTagManage'))
// 교육돌봄서비스 정보 관리
const CareServiceList = lazy(() => import('./pages/care/CareServiceList'))
const CareServiceForm = lazy(() => import('./pages/care/CareServiceForm'))
// 강사 모집공고 관리
const InstrRecrList = lazy(() => import('./pages/instrrecr/InstrRecrList'))
const InstrRecrForm = lazy(() => import('./pages/instrrecr/InstrRecrForm'))
const InstrAplyList = lazy(() => import('./pages/instrrecr/InstrAplyList'))
const InstrAplyDetail = lazy(() => import('./pages/instrrecr/InstrAplyDetail'))
// 멘토 모집공고 관리
const MentorRecrList = lazy(() => import('./pages/mentorrecr/MentorRecrList'))
const MentorRecrForm = lazy(() => import('./pages/mentorrecr/MentorRecrForm'))
const MentorAplyList = lazy(() => import('./pages/mentorrecr/MentorAplyList'))
const MentorAplyDetail = lazy(() => import('./pages/mentorrecr/MentorAplyDetail'))
// 오류 페이지
const ErrorPage = lazy(() => import('./pages/ErrorPage'))
const ForbiddenPage = lazy(() => import('./pages/ForbiddenPage'))

// Layout wrapper
// 인증 체크(ProtectedRoute) + 메뉴 R 권한 체크(MenuAccessGuard) + AdminLayout 순서로 감싼다.
// MenuAccessGuard 는 AdminLayout 안쪽에 두어 사이드바가 먼저 렌더되고, 그 안에서 404/403 구분이 가능하게 한다.
function WithLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AdminLayout>
        <MenuAccessGuard>
          <Suspense fallback={<LoadingFallback />}>
            {children}
          </Suspense>
        </MenuAccessGuard>
      </AdminLayout>
    </ProtectedRoute>
  )
}

function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Test route */}
        <Route path="/test" element={<TestPage />} />

        {/* Login */}
        <Route path="/login" element={<Login />} />
        <Route
          path="/force-password-change"
          element={<ForcePasswordChange />}
        />

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <WithLayout>
              <Dashboard />
            </WithLayout>
          }
        />

        {/* Board */}
        <Route
          path="/board"
          element={
            <WithLayout>
              <BoardList />
            </WithLayout>
          }
        />
        <Route path="/board/list" element={<Navigate to="/board" replace />} />
        <Route
          path="/board/new"
          element={
            <WithLayout>
              <BoardForm />
            </WithLayout>
          }
        />
        {/* Slug-based board content (공지사항, 센터/기관소식, 프로그램) - must be before /board/:id */}
        <Route
          path="/board/notice"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="notice" />
            </WithLayout>
          }
        />
        <Route
          path="/board/notice/content"
          element={<Navigate to="/board/notice" replace />}
        />
        <Route
          path="/board/notice/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="notice" />
            </WithLayout>
          }
        />
        <Route
          path="/board/notice/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="notice" />
            </WithLayout>
          }
        />
        <Route
          path="/board/news"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="news" />
            </WithLayout>
          }
        />
        <Route
          path="/board/news/content"
          element={<Navigate to="/board/news" replace />}
        />
        <Route
          path="/board/news/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="news" />
            </WithLayout>
          }
        />
        <Route
          path="/board/news/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="news" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program1"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="program1" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program1/content"
          element={<Navigate to="/board/program1" replace />}
        />
        <Route
          path="/board/program1/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="program1" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program1/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="program1" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program2"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="program2" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program2/content"
          element={<Navigate to="/board/program2" replace />}
        />
        <Route
          path="/board/program2/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="program2" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program2/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="program2" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program3"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="program3" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program3/content"
          element={<Navigate to="/board/program3" replace />}
        />
        <Route
          path="/board/program3/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="program3" />
            </WithLayout>
          }
        />
        <Route
          path="/board/program3/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="program3" />
            </WithLayout>
          }
        />
        {/* 커뮤니티 관리 게시판 */}
        <Route
          path="/board/promotion"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="promotion" />
            </WithLayout>
          }
        />
        <Route
          path="/board/promotion/content"
          element={<Navigate to="/board/promotion" replace />}
        />
        <Route
          path="/board/promotion/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="promotion" />
            </WithLayout>
          }
        />
        <Route
          path="/board/promotion/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="promotion" />
            </WithLayout>
          }
        />
        <Route
          path="/board/best-practice"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="best-practice" />
            </WithLayout>
          }
        />
        <Route
          path="/board/best-practice/content"
          element={<Navigate to="/board/best-practice" replace />}
        />
        <Route
          path="/board/best-practice/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="best-practice" />
            </WithLayout>
          }
        />
        <Route
          path="/board/best-practice/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="best-practice" />
            </WithLayout>
          }
        />
        <Route
          path="/board/gallery"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="gallery" />
            </WithLayout>
          }
        />
        <Route
          path="/board/gallery/content"
          element={<Navigate to="/board/gallery" replace />}
        />
        <Route
          path="/board/gallery/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="gallery" />
            </WithLayout>
          }
        />
        <Route
          path="/board/gallery/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="gallery" />
            </WithLayout>
          }
        />
        <Route
          path="/board/info-share"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="info-share" />
            </WithLayout>
          }
        />
        <Route
          path="/board/info-share/content"
          element={<Navigate to="/board/info-share" replace />}
        />
        <Route
          path="/board/info-share/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="info-share" />
            </WithLayout>
          }
        />
        <Route
          path="/board/info-share/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="info-share" />
            </WithLayout>
          }
        />
        <Route
          path="/board/:id"
          element={
            <WithLayout>
              <BoardDetail />
            </WithLayout>
          }
        />
        <Route
          path="/board/:id/edit"
          element={
            <WithLayout>
              <BoardForm />
            </WithLayout>
          }
        />
        <Route
          path="/board-content"
          element={
            <WithLayout>
              <BoardContentManageList />
            </WithLayout>
          }
        />
        <Route
          path="/board/:boardId/content"
          element={
            <WithLayout>
              <BoardContentList />
            </WithLayout>
          }
        />
        <Route
          path="/board/:boardId/content/new"
          element={
            <WithLayout>
              <BoardContentForm />
            </WithLayout>
          }
        />
        <Route
          path="/board/:boardId/content/:id"
          element={
            <WithLayout>
              <BoardContentForm />
            </WithLayout>
          }
        />
        <Route
          path="/event"
          element={
            <WithLayout>
              <EventList />
            </WithLayout>
          }
        />
        <Route
          path="/event/new"
          element={
            <WithLayout>
              <EventForm />
            </WithLayout>
          }
        />
        <Route
          path="/event/:id"
          element={
            <WithLayout>
              <EventForm />
            </WithLayout>
          }
        />
        <Route
          path="/board/faq"
          element={
            <WithLayout>
              <BoardContentListBySlug slug="faq" />
            </WithLayout>
          }
        />
        <Route
          path="/board/faq/content"
          element={<Navigate to="/board/faq" replace />}
        />
        <Route
          path="/board/faq/content/new"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="faq" />
            </WithLayout>
          }
        />
        <Route
          path="/board/faq/content/:id"
          element={
            <WithLayout>
              <BoardContentFormBySlug slug="faq" />
            </WithLayout>
          }
        />

        {/* Member */}
        <Route
          path="/profile"
          element={
            <WithLayout>
              <Profile />
            </WithLayout>
          }
        />
        <Route
          path="/manager"
          element={
            <WithLayout>
              <ManagerList />
            </WithLayout>
          }
        />
        <Route
          path="/manager/new"
          element={
            <WithLayout>
              <ManagerForm />
            </WithLayout>
          }
        />
        <Route
          path="/manager/:id"
          element={
            <WithLayout>
              <ManagerForm />
            </WithLayout>
          }
        />
        <Route
          path="/member"
          element={
            <WithLayout>
              <MemberList />
            </WithLayout>
          }
        />
        <Route
          path="/member/provider"
          element={
            <WithLayout>
              <PlaceholderPage title="공급자 회원 관리" />
            </WithLayout>
          }
        />
        <Route
          path="/member/provider/:id"
          element={
            <WithLayout>
              <ProviderMemberDetail />
            </WithLayout>
          }
        />
        <Route
          path="/member/school"
          element={
            <WithLayout>
              <SchoolMemberList />
            </WithLayout>
          }
        />
        <Route
          path="/member/school/new"
          element={
            <WithLayout>
              <SchoolMemberForm />
            </WithLayout>
          }
        />
        <Route
          path="/member/school/:id"
          element={
            <WithLayout>
              <SchoolMemberForm />
            </WithLayout>
          }
        />
        <Route
          path="/member/noshow"
          element={
            <WithLayout>
              <NoShowMemberList />
            </WithLayout>
          }
        />
        <Route
          path="/member/:id"
          element={
            <WithLayout>
              <MemberDetail />
            </WithLayout>
          }
        />
        <Route
          path="/authority"
          element={
            <WithLayout>
              <AuthorityList />
            </WithLayout>
          }
        />
        <Route
          path="/authority-menu"
          element={
            <WithLayout>
              <AuthorityMenuAssign />
            </WithLayout>
          }
        />
        <Route
          path="/manager-auth"
          element={
            <WithLayout>
              <ManagerAuthAssign />
            </WithLayout>
          }
        />
        <Route
          path="/login-history"
          element={
            <WithLayout>
              <LoginHistoryList />
            </WithLayout>
          }
        />
        <Route
          path="/info-query-history"
          element={
            <WithLayout>
              <InfoQueryHistoryList />
            </WithLayout>
          }
        />
        <Route
          path="/info-change-history"
          element={
            <WithLayout>
              <InfoChangeHistoryList />
            </WithLayout>
          }
        />

        {/* Reservation/Application */}
        <Route
          path="/counseling"
          element={
            <WithLayout>
              <CounselingList />
            </WithLayout>
          }
        />
        <Route
          path="/counseling/:id"
          element={
            <WithLayout>
              <CounselingDetail />
            </WithLayout>
          }
        />
        <Route
          path="/care-reservation"
          element={
            <WithLayout>
              <CareReservationList />
            </WithLayout>
          }
        />
        <Route
          path="/care-reservation/:id"
          element={
            <WithLayout>
              <CareReservationDetail />
            </WithLayout>
          }
        />

        {/* Facility & Information */}
        <Route
          path="/facility"
          element={
            <WithLayout>
              <FacilityList />
            </WithLayout>
          }
        />
        <Route
          path="/facility/care"
          element={
            <WithLayout>
              <CareFacilityList />
            </WithLayout>
          }
        />
        <Route
          path="/facility/care/new"
          element={
            <WithLayout>
              <CareFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/care/edit/:id"
          element={
            <WithLayout>
              <CareFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/experience"
          element={
            <WithLayout>
              <ExperienceFacilityList />
            </WithLayout>
          }
        />
        <Route
          path="/facility/experience/new"
          element={
            <WithLayout>
              <ExperienceFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/experience/edit/:id"
          element={
            <WithLayout>
              <ExperienceFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/tourism"
          element={
            <WithLayout>
              <TourismFacilityList />
            </WithLayout>
          }
        />
        <Route
          path="/facility/tourism/new"
          element={
            <WithLayout>
              <TourismFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/tourism/edit/:id"
          element={
            <WithLayout>
              <TourismFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/medical"
          element={
            <WithLayout>
              <MedicalFacilityList />
            </WithLayout>
          }
        />
        <Route
          path="/facility/medical/new"
          element={
            <WithLayout>
              <MedicalFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/medical/edit/:id"
          element={
            <WithLayout>
              <MedicalFacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/new"
          element={
            <WithLayout>
              <FacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/facility/edit/:id"
          element={
            <WithLayout>
              <FacilityForm />
            </WithLayout>
          }
        />
        <Route
          path="/welfare"
          element={
            <WithLayout>
              <WelfareList />
            </WithLayout>
          }
        />
        <Route
          path="/welfare/new"
          element={
            <WithLayout>
              <WelfareForm />
            </WithLayout>
          }
        />
        <Route
          path="/welfare/edit/:id"
          element={
            <WithLayout>
              <WelfareForm />
            </WithLayout>
          }
        />
        <Route
          path="/program"
          element={
            <WithLayout>
              <ProgramList />
            </WithLayout>
          }
        />
        <Route
          path="/program/new"
          element={
            <WithLayout>
              <ProgramForm />
            </WithLayout>
          }
        />
        <Route
          path="/program/:id"
          element={
            <WithLayout>
              <ProgramForm />
            </WithLayout>
          }
        />

        {/* Homepage Management */}
        <Route
          path="/main"
          element={
            <WithLayout>
              <MainList />
            </WithLayout>
          }
        />
        <Route
          path="/main/new"
          element={
            <WithLayout>
              <MainForm />
            </WithLayout>
          }
        />
        <Route
          path="/main/:id"
          element={
            <WithLayout>
              <MainForm />
            </WithLayout>
          }
        />
        <Route
          path="/html-page"
          element={
            <WithLayout>
              <HtmlContentList />
            </WithLayout>
          }
        />
        <Route
          path="/html-page/new"
          element={
            <WithLayout>
              <HtmlContentForm />
            </WithLayout>
          }
        />
        <Route
          path="/html-page/:id"
          element={
            <WithLayout>
              <HtmlContentForm />
            </WithLayout>
          }
        />
        <Route
          path="/sms-history"
          element={
            <WithLayout>
              <SmsHistoryList />
            </WithLayout>
          }
        />
        <Route
          path="/satisfaction"
          element={
            <WithLayout>
              <SatisfactionList />
            </WithLayout>
          }
        />

        {/* Statistics - 홈페이지 접속통계 */}
        <Route
          path="/stat/access"
          element={
            <WithLayout>
              <AccessStatistics />
            </WithLayout>
          }
        />
        <Route
          path="/stat/access/daily"
          element={
            <WithLayout>
              <HourStat />
            </WithLayout>
          }
        />
        <Route
          path="/stat/access/monthly"
          element={
            <WithLayout>
              <MnthStat />
            </WithLayout>
          }
        />
        <Route
          path="/stat/access/yearly"
          element={
            <WithLayout>
              <YearStat />
            </WithLayout>
          }
        />
        {/* Statistics - 페이지별 통계 */}
        <Route
          path="/stat/page"
          element={
            <WithLayout>
              <PageStatistics />
            </WithLayout>
          }
        />
        <Route
          path="/stat/page/daily"
          element={
            <WithLayout>
              <MenuDaysStat />
            </WithLayout>
          }
        />
        <Route
          path="/stat/page/monthly"
          element={
            <WithLayout>
              <MenuMontStat />
            </WithLayout>
          }
        />
        {/* Statistics - 관리자접속이력 */}
        <Route
          path="/stat/admin-log"
          element={
            <WithLayout>
              <AdminAccessLog />
            </WithLayout>
          }
        />
        {/* Statistics - 돌봄시설별접속로그조회 */}
        <Route
          path="/stat/facility-log"
          element={
            <WithLayout>
              <FacilityLog />
            </WithLayout>
          }
        />
        {/* Statistics - 예약·상담 신청통계 */}
        <Route
          path="/stat/reservation"
          element={
            <WithLayout>
              <ReservationStat />
            </WithLayout>
          }
        />
        {/* Statistics - 에러로그조회 */}
        <Route
          path="/stat/error-log"
          element={
            <WithLayout>
              <ErrorLog />
            </WithLayout>
          }
        />

        {/* Content */}
        <Route
          path="/banner"
          element={
            <WithLayout>
              <BannerList />
            </WithLayout>
          }
        />
        <Route
          path="/banner/new"
          element={
            <WithLayout>
              <BannerForm />
            </WithLayout>
          }
        />
        <Route
          path="/banner/:id"
          element={
            <WithLayout>
              <BannerForm />
            </WithLayout>
          }
        />
        <Route
          path="/popup"
          element={
            <WithLayout>
              <PopupList />
            </WithLayout>
          }
        />
        <Route
          path="/popup/new"
          element={
            <WithLayout>
              <PopupForm />
            </WithLayout>
          }
        />
        <Route
          path="/popup/:id"
          element={
            <WithLayout>
              <PopupForm />
            </WithLayout>
          }
        />
        <Route
          path="/faq"
          element={
            <WithLayout>
              <FaqList />
            </WithLayout>
          }
        />
        <Route
          path="/faq/new"
          element={
            <WithLayout>
              <FaqForm />
            </WithLayout>
          }
        />
        <Route
          path="/faq/:id"
          element={
            <WithLayout>
              <FaqForm />
            </WithLayout>
          }
        />

        {/* Code */}
        <Route
          path="/code"
          element={
            <WithLayout>
              <CodeList />
            </WithLayout>
          }
        />
        <Route
          path="/code/new"
          element={
            <WithLayout>
              <CodeTypeForm />
            </WithLayout>
          }
        />
        <Route
          path="/code/:id"
          element={
            <WithLayout>
              <CodeDetail />
            </WithLayout>
          }
        />

        {/* Menu */}
        <Route
          path="/menu"
          element={
            <WithLayout>
              <MenuList />
            </WithLayout>
          }
        />
        <Route
          path="/sitemap"
          element={
            <WithLayout>
              <SitemapPage />
            </WithLayout>
          }
        />

        {/* AI Chatbot routes — CHATBOT DISABLED
        <Route path="/chatbot/dashboard/documents" element={<WithLayout><ChatbotDocuments /></WithLayout>} />
        <Route path="/chatbot/dashboard/documents/upload" element={<WithLayout><ChatbotDocumentUpload /></WithLayout>} />
        <Route path="/chatbot/dashboard/categories" element={<WithLayout><ChatbotCategories /></WithLayout>} />
        <Route path="/chatbot/dashboard/audit-logs" element={<WithLayout><ChatbotAuditLogs /></WithLayout>} />
        <Route path="/chatbot/dashboard/usage" element={<WithLayout><ChatbotUsage /></WithLayout>} />
        <Route path="/chatbot/dashboard/playground" element={<WithLayout><ChatbotPlayground /></WithLayout>} />
        <Route path="/chatbot/dashboard/settings" element={<WithLayout><ChatbotSettingsPage /></WithLayout>} />
        <Route path="/chatbot/dashboard/qna" element={<WithLayout><ChatbotQnaList /></WithLayout>} />
        <Route path="/chatbot/dashboard/qna/new" element={<WithLayout><ChatbotQnaForm /></WithLayout>} />
        <Route path="/chatbot/dashboard/qna/:id" element={<WithLayout><ChatbotQnaForm /></WithLayout>} />
        <Route path="/chatbot/dashboard/qna/categories" element={<WithLayout><ChatbotQnaCategories /></WithLayout>} />
        <Route path="/chatbot/dashboard/qna/tags" element={<WithLayout><ChatbotQnaTagManage /></WithLayout>} />
        <Route path="/chatbot/dashboard" element={<WithLayout><ChatbotDashboard /></WithLayout>} />
        <Route path="/chatbot/documents" element={<WithLayout><ChatbotDocuments /></WithLayout>} />
        <Route path="/chatbot/documents/upload" element={<WithLayout><ChatbotDocumentUpload /></WithLayout>} />
        <Route path="/chatbot/categories" element={<WithLayout><ChatbotCategories /></WithLayout>} />
        <Route path="/chatbot/audit-logs" element={<WithLayout><ChatbotAuditLogs /></WithLayout>} />
        <Route path="/chatbot/usage" element={<WithLayout><ChatbotUsage /></WithLayout>} />
        <Route path="/chatbot/playground" element={<WithLayout><ChatbotPlayground /></WithLayout>} />
        <Route path="/chatbot/settings" element={<WithLayout><ChatbotSettingsPage /></WithLayout>} />
        <Route path="/chatbot/qna" element={<WithLayout><ChatbotQnaList /></WithLayout>} />
        <Route path="/chatbot/qna/new" element={<WithLayout><ChatbotQnaForm /></WithLayout>} />
        <Route path="/chatbot/qna/:id" element={<WithLayout><ChatbotQnaForm /></WithLayout>} />
        <Route path="/chatbot/qna/categories" element={<WithLayout><ChatbotQnaCategories /></WithLayout>} />
        <Route path="/chatbot/qna/tags" element={<WithLayout><ChatbotQnaTagManage /></WithLayout>} />
        */}

        {/* Placeholder - 홈페이지 관리 (미개발) */}
        <Route
          path="/recommend-program"
          element={
            <WithLayout>
              <RecommendProgramList />
            </WithLayout>
          }
        />
        <Route
          path="/recommend-program/new"
          element={
            <WithLayout>
              <RecommendProgramForm />
            </WithLayout>
          }
        />
        <Route
          path="/recommend-program/:id"
          element={
            <WithLayout>
              <RecommendProgramForm />
            </WithLayout>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <WithLayout>
              <PrivacyPolicyList />
            </WithLayout>
          }
        />
        <Route
          path="/privacy-policy/new"
          element={
            <WithLayout>
              <PrivacyPolicyForm />
            </WithLayout>
          }
        />
        <Route
          path="/privacy-policy/:id"
          element={
            <WithLayout>
              <PrivacyPolicyForm />
            </WithLayout>
          }
        />

        {/* Placeholder - 프로그램 관리 (미개발) */}
        <Route
          path="/program-application"
          element={
            <WithLayout>
              <ProgramApplicationList />
            </WithLayout>
          }
        />
        <Route
          path="/program-application/new"
          element={
            <WithLayout>
              <ProgramApplicationForm />
            </WithLayout>
          }
        />
        <Route
          path="/program-application/:aplyNo/:prgmNo"
          element={
            <WithLayout>
              <ProgramApplicationForm />
            </WithLayout>
          }
        />
        <Route
          path="/program-satisfaction"
          element={
            <WithLayout>
              <ProgramSatisfactionList />
            </WithLayout>
          }
        />
        <Route
          path="/program-satisfaction/:prgmNo/results"
          element={
            <WithLayout>
              <SatisfactionResultList />
            </WithLayout>
          }
        />
        <Route
          path="/program-satisfaction/:prgmNo/results/:rsltNo"
          element={
            <WithLayout>
              <SatisfactionResultDetail />
            </WithLayout>
          }
        />
        <Route
          path="/program-satisfaction/:prgmNo/stats"
          element={
            <WithLayout>
              <SatisfactionStatsPage />
            </WithLayout>
          }
        />

        {/* 돌봄정보 관리 */}
        <Route
          path="/care-service"
          element={
            <WithLayout>
              <CareServiceList />
            </WithLayout>
          }
        />
        <Route
          path="/care-service/new"
          element={
            <WithLayout>
              <CareServiceForm />
            </WithLayout>
          }
        />
        <Route
          path="/care-service/edit/:id"
          element={
            <WithLayout>
              <CareServiceForm />
            </WithLayout>
          }
        />
        <Route
          path="/instructor-recruit"
          element={
            <WithLayout>
              <InstrRecrList />
            </WithLayout>
          }
        />
        <Route
          path="/instructor-recruit/new"
          element={
            <WithLayout>
              <InstrRecrForm />
            </WithLayout>
          }
        />
        <Route
          path="/instructor-recruit/edit/:id"
          element={
            <WithLayout>
              <InstrRecrForm />
            </WithLayout>
          }
        />
        <Route
          path="/instructor-recruit/:instrRecrNo/applicants"
          element={
            <WithLayout>
              <InstrAplyList />
            </WithLayout>
          }
        />
        <Route
          path="/instructor-recruit/:instrRecrNo/applicants/:instrAplyNo"
          element={
            <WithLayout>
              <InstrAplyDetail />
            </WithLayout>
          }
        />
        <Route
          path="/mentor-recruit"
          element={
            <WithLayout>
              <MentorRecrList />
            </WithLayout>
          }
        />
        <Route
          path="/mentor-recruit/new"
          element={
            <WithLayout>
              <MentorRecrForm />
            </WithLayout>
          }
        />
        <Route
          path="/mentor-recruit/edit/:id"
          element={
            <WithLayout>
              <MentorRecrForm />
            </WithLayout>
          }
        />
        <Route
          path="/mentor-recruit/:recrId/applicants"
          element={
            <WithLayout>
              <MentorAplyList />
            </WithLayout>
          }
        />
        <Route
          path="/mentor-recruit/:recrId/applicants/:aplyId"
          element={
            <WithLayout>
              <MentorAplyDetail />
            </WithLayout>
          }
        />

        {/* Placeholder - 통계 관리 (미개발) */}
        <Route
          path="/stat/care-program"
          element={
            <WithLayout>
              <CareProgramStat />
            </WithLayout>
          }
        />
        <Route
          path="/stat/member"
          element={
            <WithLayout>
              <MemberStat />
            </WithLayout>
          }
        />
        <Route
          path="/stat/admin-auth-log"
          element={
            <WithLayout>
              <PlaceholderPage title="관리자 권한이력" />
            </WithLayout>
          }
        />

        {/* Dev Tools */}
        <Route
          path="/dev/ui-showcase"
          element={
            <WithLayout>
              <UIShowcase />
            </WithLayout>
          }
        />

        {/* Error Page */}
        <Route path="/error" element={<ErrorPage />} />

        {/* 403 Forbidden — MenuAccessGuard 에서 리다이렉트 타깃.
            WithLayout 을 씌우지 않아 무한 리다이렉트를 방지한다. */}
        <Route path="/forbidden" element={<ForbiddenPage />} />

        {/* Catch all */}
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </Suspense>
  );
}

export default App
